import { api_ } from '@shared/plugIn/axios';

import {
  AddconCancelByOrderResult,
  AddconConfirmResult,
  AddconCouponIssuesResponse,
  AddconEnvironment,
  AddconGatewayIssueItem,
  AddconGatewayIssueList,
  AddconGatewayIssueQuery,
  AddconGoods,
  AddconIssueRecord,
  AddconIssueState,
  AddconMappingResult,
  AddconMappingTarget,
  AddconMappingUpdate,
  AddconResolveBody,
  AddconRetryDeliverResult,
} from './giftCouponAddcon.interface';

/**
 * 애드콘 API 발송 백엔드(/admin/gift-coupon-addcon). Admin·GiftAgent 조회, 매핑 쓰기는 Admin.
 * 계약이 바뀌면 이 파일만 고친다. 구서버(라우트 없음)는 404 → 호출부가 "미배포" 안내.
 */
export const GIFT_COUPON_ADDCON_PREFIX = '/admin/gift-coupon-addcon';

export const isAddconUnsupported = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 404;

/** 백엔드가 400 등으로 준 메시지 (전역 필터가 code는 지우고 message만 남긴다) */
export const addconErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: { message?: unknown } } })?.response?.data;
  const message = data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return typeof message === 'string' && message ? message : fallback;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** 계약 상품 목록 — 배열 또는 { items | goods } 어느 쪽이든 배열로 */
export const fetchAddconGoods = async (): Promise<AddconGoods[]> => {
  const { data } = await api_.get<unknown>(`${GIFT_COUPON_ADDCON_PREFIX}/goods`);
  if (Array.isArray(data)) return data as AddconGoods[];
  if (isRecord(data)) {
    const list = data.items ?? data.goods ?? data.data;
    if (Array.isArray(list)) return list as AddconGoods[];
  }
  return [];
};

//* 상품 목록은 화면 여러 곳(연결 패널·카탈로그)이 같은 세션 안에서 쓴다 — 1회 조회를 공유
let goodsCache: { at: number; promise: Promise<AddconGoods[]> } | null = null;
const GOODS_CACHE_MS = 5 * 60 * 1000;

export const fetchAddconGoodsCached = (force = false): Promise<AddconGoods[]> => {
  if (!force && goodsCache && Date.now() - goodsCache.at < GOODS_CACHE_MS) {
    return goodsCache.promise;
  }
  const promise = fetchAddconGoods().catch((error) => {
    goodsCache = null;
    throw error;
  });
  goodsCache = { at: Date.now(), promise };
  return promise;
};

export const fetchAddconEnvironment = async (): Promise<AddconEnvironment | null> => {
  try {
    const { data } = await api_.get<AddconEnvironment>(
      `${GIFT_COUPON_ADDCON_PREFIX}/environment`,
    );
    return isRecord(data) ? data : null;
  } catch (error) {
    if (isAddconUnsupported(error)) return null;
    throw error;
  }
};

/**
 * 상품의 발송 매체·애드콘 계약번호 저장(upsert) — 한 요청에 둘 다. 상품 PATCH로 deliveryChannel을 바꾸지 않는다.
 * noReq는 서버가 중개서버에 상품 존재를 확인한 뒤 저장한다(없는 번호 400).
 */
export const setAddconMapping = async (
  target: AddconMappingTarget,
  id: string,
  body: AddconMappingUpdate,
): Promise<AddconMappingResult> => {
  const { data } = await api_.put<AddconMappingResult>(
    `${GIFT_COUPON_ADDCON_PREFIX}/mapping/${target}/${id}`,
    body,
  );
  return data;
};

/** 발송 매체·계약번호 모두 삭제(미설정=수동) */
export const clearAddconMapping = async (
  target: AddconMappingTarget,
  id: string,
): Promise<AddconMappingResult> => {
  const { data } = await api_.delete<AddconMappingResult>(
    `${GIFT_COUPON_ADDCON_PREFIX}/mapping/${target}/${id}`,
  );
  return data;
};

// ---------------------------------------------------------------------------
// 발행 기록 조회·확인·복구 (스펙 §4) — 대시보드 /gift-api
// ---------------------------------------------------------------------------

/** 응답이 배열이든 { items | issues | data } 래퍼든 배열로 */
const unwrapList = <T>(data: unknown): T[] => {
  if (Array.isArray(data)) return data as T[];
  if (isRecord(data)) {
    const list = data.items ?? data.issues ?? data.data;
    if (Array.isArray(list)) return list as T[];
  }
  return [];
};

/** 사람이 봐야 하는 발행 기록 (attention 사유 포함, coupon populate) */
export const fetchAddconAttention = async (): Promise<AddconIssueRecord[]> => {
  const { data } = await api_.get<unknown>(`${GIFT_COUPON_ADDCON_PREFIX}/attention`);
  return unwrapList<AddconIssueRecord>(data);
};

/** 상태별 발행 기록 (최대 500, 과거 시도 포함). state 생략 = 전체(서버가 허용할 때) */
export const fetchAddconIssueRecords = async (params: {
  state?: AddconIssueState;
  limit?: number;
}): Promise<AddconIssueRecord[]> => {
  const search = new URLSearchParams();
  if (params.state) search.set('state', params.state);
  if (params.limit) search.set('limit', String(params.limit));
  const query = search.toString();
  const { data } = await api_.get<unknown>(
    `${GIFT_COUPON_ADDCON_PREFIX}/coupons${query ? `?${query}` : ''}`,
  );
  return unwrapList<AddconIssueRecord>(data);
};

/** 쿠폰 1장의 발행 기록 전부(시도 순) */
export const fetchAddconCouponIssues = async (
  couponId: string,
): Promise<AddconCouponIssuesResponse> => {
  const { data } = await api_.get<AddconCouponIssuesResponse>(
    `${GIFT_COUPON_ADDCON_PREFIX}/coupons/${encodeURIComponent(couponId)}/issues`,
  );
  return data;
};

/** 미확정 확인: 중개서버 저장결과 → 주문번호 다우 조회. issued면 발송완료 전환까지 */
export const confirmAddconIssue = async (
  couponId: string,
): Promise<AddconConfirmResult> => {
  const { data } = await api_.post<AddconConfirmResult>(
    `${GIFT_COUPON_ADDCON_PREFIX}/coupons/${encodeURIComponent(couponId)}/confirm`,
  );
  return data;
};

/** 운영자 판정(애드콘 사이트에서 확인한 결과를 기록) */
export const resolveAddconIssue = async (
  couponId: string,
  body: AddconResolveBody,
): Promise<unknown> => {
  const { data } = await api_.post<unknown>(
    `${GIFT_COUPON_ADDCON_PREFIX}/coupons/${encodeURIComponent(couponId)}/resolve`,
    body,
  );
  return data;
};

/** 미확정 건을 주문번호로 다우 취소 (쿠폰번호 없는 건의 유일한 열쇠) */
export const cancelAddconByOrder = async (
  couponId: string,
  reason?: string,
): Promise<AddconCancelByOrderResult> => {
  const { data } = await api_.post<AddconCancelByOrderResult>(
    `${GIFT_COUPON_ADDCON_PREFIX}/coupons/${encodeURIComponent(
      couponId,
    )}/cancel-by-order`,
    reason ? { reason } : {},
  );
  return data;
};

/** 발행 성공 후 전환 실패 — 다우 호출 없이 발송완료 전환만 */
export const retryAddconDeliver = async (
  couponId: string,
): Promise<AddconRetryDeliverResult> => {
  const { data } = await api_.post<AddconRetryDeliverResult>(
    `${GIFT_COUPON_ADDCON_PREFIX}/coupons/${encodeURIComponent(couponId)}/retry-deliver`,
  );
  return data;
};

/**
 * 중개서버에 저장된 전체 발행 목록(기간·커서) — 백엔드 프록시 GET /gateway-issues.
 * 중개서버(요청 5)·백엔드 어느 쪽이든 미반영이면 null(404).
 */
export const fetchAddconGatewayIssues = async (
  query: AddconGatewayIssueQuery,
): Promise<AddconGatewayIssueList | null> => {
  const search = new URLSearchParams();
  if (query.from) search.set('from', query.from);
  if (query.to) search.set('to', query.to);
  if (query.after) search.set('after', query.after);
  if (query.limit) search.set('limit', String(query.limit));
  if (query.status) search.set('status', query.status);
  const qs = search.toString();
  try {
    const { data } = await api_.get<unknown>(
      `${GIFT_COUPON_ADDCON_PREFIX}/gateway-issues${qs ? `?${qs}` : ''}`,
    );
    //* { items, next, hasMore, summary } 또는 중개서버 봉투 { data: {...} } 그대로일 때
    const payload =
      isRecord(data) && isRecord(data.data) && Array.isArray(data.data.items)
        ? data.data
        : data;
    if (!isRecord(payload)) return { items: [], next: null, hasMore: false };
    return {
      items: unwrapList<AddconGatewayIssueItem>(payload),
      next: typeof payload.next === 'string' && payload.next ? payload.next : null,
      hasMore: payload.hasMore === true,
      summary: isRecord(payload.summary)
        ? (payload.summary as AddconGatewayIssueList['summary'])
        : undefined,
    };
  } catch (error) {
    if (isAddconUnsupported(error)) return null;
    throw error;
  }
};
